import { useMemo } from "react";

import { Indicator, NavLink as MantineNavLink } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import {
	IconBell,
	IconBriefcase,
	IconChartBar,
	IconClipboardPlus,
	IconFileExport,
	IconHome,
	IconLock,
	IconMap,
	IconMessageCircle,
	IconReport,
	IconTicket,
	IconUser,
	IconUsers,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { NavLink, useLocation, useParams } from "react-router";

import {
	useGetChatUnreadCountQuery,
	useGetInAppNotificationsQuery,
} from "@/setup/frontend/api/apiHooks";
import IfAdmin from "@/users/components/IfAdmin";
import { IfDeveloper } from "@/users/components/IfDeveloper";
import { IfTicketAccess } from "@/tickets/components/if-ticket-access";
import { useTRPC } from "@/common/utils/trpc";

import { ROUTES } from "@/setup/frontend/routes";

export default function MainNav() {
	const params = useParams();
	const { pathname } = useLocation();
	const trpc = useTRPC();
	const notifications = useGetInAppNotificationsQuery();
	const [latestViewedNotification] = useLocalStorage({
		key: "vcg-latest-viewed-notification",
		defaultValue: "",
	});
	const showNotificationIndicator = useMemo(() => {
		return (
			notifications.data &&
			notifications.data[0] &&
			`${notifications.data[0].type}-${notifications.data[0].id}` !==
				latestViewedNotification
		);
	}, [notifications.data, latestViewedNotification]);
	const unreadCount = useGetChatUnreadCountQuery();
	const ticketAccess = useQuery(trpc.tickets.hasAccess.queryOptions());
	const ticketUnread = useQuery({
		...trpc.tickets.notificationsUnreadCount.queryOptions(),
		refetchInterval: 60_000,
		enabled: !!ticketAccess.data,
	});

	return (
		<>
			<MantineNavLink
				component={NavLink}
				to={ROUTES.HOME.ROOT}
				leftSection={<IconHome size={20} stroke={1.5} />}
				label="Home"
			/>
			<MantineNavLink
				component={NavLink}
				to={ROUTES.DASHBOARD.ROOT}
				leftSection={<IconChartBar size={20} stroke={1.5} />}
				label="Dashboard"
			/>
			<MantineNavLink
				component={NavLink}
				to={ROUTES.MAP.ROOT}
				leftSection={<IconMap size={20} stroke={1.5} />}
				label="Map"
			/>
			<MantineNavLink
				component={NavLink}
				to={ROUTES.CLIENTS.ROOT}
				leftSection={<IconUser size={20} stroke={1.5} />}
				label="Clients"
				opened={pathname.startsWith("/clients/")}
			>
				{params.id && pathname.startsWith("/clients/") && (
					<>
						<MantineNavLink
							component={NavLink}
							to={ROUTES.CLIENTS.SCHEDULE(params.id)}
							label="Schedule"
						/>
						<MantineNavLink
							component={NavLink}
							to={ROUTES.CLIENTS.PROFILE(params.id)}
							label="Profile"
						/>
					</>
				)}
			</MantineNavLink>
			<MantineNavLink
				component={NavLink}
				to={ROUTES.CAREGIVERS.ROOT}
				leftSection={<IconUsers size={20} stroke={1.5} />}
				label="Caregivers"
				opened={pathname.startsWith("/caregivers/")}
			>
				{params.id && pathname.startsWith("/caregivers/") && (
					<>
						<MantineNavLink
							component={NavLink}
							to={ROUTES.CAREGIVERS.SCHEDULE(params.id)}
							label="Schedule"
						/>
						<MantineNavLink
							component={NavLink}
							to={ROUTES.CAREGIVERS.PROFILE(params.id)}
							label="Profile"
						/>
					</>
				)}
			</MantineNavLink>
			<MantineNavLink
				component={NavLink}
				to="/reports"
				leftSection={<IconReport size={20} stroke={1.5} />}
				label="Reports"
				defaultOpened={pathname.startsWith("/reports")}
			/>
			<MantineNavLink
				leftSection={<IconFileExport size={20} stroke={1.5} />}
				label="Files"
				href="#files"
				defaultOpened={pathname.startsWith("/file")}
			>
				<MantineNavLink component={NavLink} label="Import" to="/file-imports" />
				<MantineNavLink component={NavLink} label="Export" to="/file-exports" />
			</MantineNavLink>
			<MantineNavLink
				leftSection={<IconBriefcase size={20} stroke={1.5} />}
				label="Cases"
				to={ROUTES.CASES.ROOT}
				component={NavLink}
			/>
			<MantineNavLink
				leftSection={<IconClipboardPlus size={20} stroke={1.5} />}
				label="Care Forms"
				to={ROUTES.CARE_FORMS.ROOT}
				component={NavLink}
			/>
			<IfTicketAccess>
				<MantineNavLink
					leftSection={
						<Indicator
							color="red"
							disabled={!ticketUnread.data?.count}
							label={ticketUnread.data?.count}
							size={16}
							position="top-end"
						>
							<IconTicket size={20} stroke={1.5} />
						</Indicator>
					}
					label="Tickets"
					to={ROUTES.TICKETS.ROOT}
					component={NavLink}
				/>
			</IfTicketAccess>
			<IfAdmin>
				<MantineNavLink
					href="#admin"
					leftSection={<IconLock size={20} stroke={1.5} />}
					label="Admin"
					defaultOpened={pathname.startsWith("/admin")}
				>
					<MantineNavLink
						component={NavLink}
						label="Branches"
						to={ROUTES.ADMIN.BRANCHES}
					/>
					<MantineNavLink
						component={NavLink}
						label="Users"
						to={ROUTES.ADMIN.USERS}
					/>
					<MantineNavLink
						component={NavLink}
						label="Roles"
						to={ROUTES.ADMIN.ROLES}
					/>
					<MantineNavLink
						component={NavLink}
						label="Payer Sources"
						to={ROUTES.ADMIN.PAYER_SOURCES}
					/>
					<MantineNavLink
						component={NavLink}
						label="VA Medical Centers"
						to={ROUTES.ADMIN.VA_MEDICAL_CENTERS}
					/>
					<MantineNavLink
						component={NavLink}
						label="Pay Period Lock Times"
						to={ROUTES.ADMIN.PAY_PERIOD_LOCK_TIMES}
					/>
					<MantineNavLink
						component={NavLink}
						label="Care Form Types"
						to={ROUTES.CARE_FORMS.CARE_FORM_TYPES.ROOT}
					/>
					<IfDeveloper>
						<MantineNavLink
							component={NavLink}
							label="File Export Types"
							to={ROUTES.ADMIN.FILE_EXPORT_TYPES}
						/>
						<MantineNavLink
							component={NavLink}
							label="File Import Types"
							to={ROUTES.ADMIN.FILE_IMPORT_TYPES}
						/>
						<MantineNavLink
							component={NavLink}
							label="Logging"
							to={ROUTES.ADMIN.LOGGING}
						/>
						<MantineNavLink
							component={NavLink}
							label="Feature Flags"
							to={ROUTES.ADMIN.FEATURE_FLAGS}
						/>
					</IfDeveloper>
				</MantineNavLink>
			</IfAdmin>
			<MantineNavLink
				component={NavLink}
				leftSection={
					<Indicator
						color="red"
						disabled={!showNotificationIndicator}
						position="top-end"
						size={10}
					>
						<IconBell size={20} stroke={1.5} />
					</Indicator>
				}
				label="Notifications"
				to="/notifications"
			/>
			<MantineNavLink
				leftSection={
					<Indicator
						color="red"
						disabled={!unreadCount.data?.unreadCount}
						label={unreadCount.data?.unreadCount}
						size={16}
						position="top-end"
					>
						<IconMessageCircle size={20} stroke={1.5} />
					</Indicator>
				}
				label="Chat"
				to="/chat"
				component={NavLink}
			/>
		</>
	);
}
